import { FC } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { captureException } from '@sentry/react';
import { Avatar, Button, Col, Row } from 'antd';
import { TbArrowRight, TbArrowUpRight, TbPlug, TbPuzzle, TbRosetteDiscountCheckFilled } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useMissingFeatureAddOnsRequired } from '@/hooks/useMissingFeatureAddOnsRequired';
import { usePlanUsageLimits } from '@/hooks/usePlanUsageLimits';
import { EnrichedAnchorTag, VirtualizedList as List, PlanTag, SmallTag, SmallTooltip } from '@/shared';
import RequiredAddOnSmallTag from '@/shared/components/tags/RequiredAddOnSmallTag';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useBillingStore } from '@/stores/database/aliases/billing';
import { imgLogoStyling } from '@/theme';
import { DataSourceCategory, DataSourceOption, SetStateMethod } from '@/types';

interface AddDataSourceListItemProps {
    connecting: boolean;
    i: DataSourceOption;
    indx: number;
    interactionDisabled: boolean;
    setOpen: SetStateMethod<boolean>;
    onBeginConnecting: (optionKey: string) => boolean;
    onFinishConnecting: (optionKey: string) => void;
}

const AddDataSourceListItem: FC<AddDataSourceListItemProps> = ({
    connecting,
    i,
    indx,
    interactionDisabled,
    setOpen,
    onBeginConnecting,
    onFinishConnecting,
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    const { loading } = useBillingStore();
    const { hasMinimumPlan } = useEntitlements();
    const { getUsageState } = usePlanUsageLimits();
    const dataSourceUsage = getUsageState('dataSources');

    const missingRequiredAddOn = useMissingFeatureAddOnsRequired(i.requiredAddOnsForUse);
    const missingRequiredPlan = Boolean(i.minimumPlanForUse && !hasMinimumPlan(i.minimumPlanForUse));

    const handleConnect = async () => {
        const optionKey = String(i.key);

        if (
            connecting ||
            interactionDisabled ||
            dataSourceUsage.reached ||
            missingRequiredAddOn ||
            missingRequiredPlan ||
            !onBeginConnecting(optionKey)
        ) {
            return;
        }

        try {
            await i.handler();
        } catch (error) {
            captureException(error);
            toast.error('Unable to start data source connection');
        } finally {
            onFinishConnecting(optionKey);
        }
    };

    const navigate = useNavigate();

    const disabled = missingRequiredPlan;

    return (
        <List.Item key={i?.key} style={{ paddingTop: indx === 0 ? 0 : '0.5rem' }}>
            <List.Item.Meta
                avatar={
                    <div style={{ marginTop: '0.25rem' }}>
                        <Avatar
                            size={'large'}
                            icon={
                                i?.src ? (
                                    <img
                                        alt=''
                                        src={i?.src}
                                        style={{
                                            ...i?.avatarStyling,
                                            ...imgLogoStyling,
                                        }}
                                    />
                                ) : (
                                    i?.icon
                                )
                            }
                            shape='square'
                            style={{
                                backgroundColor: token.colorBgBase,
                                borderRadius: token.borderRadius,
                                border: `1px solid ${token.colorBorder}`,
                                marginRight: '-0.25rem',
                            }}
                        />
                    </div>
                }
                title={
                    <span style={{ fontWeight: 500 }}>
                        {i?.label}
                        {i?.officialIntegration && (
                            <SmallTooltip
                                title={
                                    <>
                                        This is an official integration built & managed by{' '}
                                        <EnrichedAnchorTag label='Countercyclical' url='https://countercyclical.com' />
                                    </>
                                }
                                placement='right'
                                padding='0 0.25rem'
                            >
                                <TbRosetteDiscountCheckFilled
                                    aria-label='Official Countercyclical integration'
                                    fill={token.gold6}
                                    // stroke={token.gold6}
                                    style={{
                                        cursor: 'help',
                                        verticalAlign: '-0.1rem',
                                        margin: '0 0.5rem',
                                    }}
                                />
                            </SmallTooltip>
                        )}
                        {missingRequiredAddOn && !loading ? (
                            <RequiredAddOnSmallTag i={i} />
                        ) : missingRequiredPlan && i.minimumPlanForUse ? (
                            <PlanTag plan={i.minimumPlanForUse} style={{ marginLeft: '0.5rem' }} />
                        ) : loading ? (
                            <SmallTag
                                message={
                                    <span style={{ color: token.colorTextTertiary }}>
                                        <LoadingOutlined spin style={{ marginRight: '0.375rem' }} />
                                        Loading...
                                    </span>
                                }
                                styles={{ marginLeft: 0 }}
                            />
                        ) : null}
                    </span>
                }
                description={
                    <Row align='middle'>
                        <Col flex='none'>
                            {i?.categories?.map((category: DataSourceCategory) => {
                                return (
                                    <SmallTag
                                        key={category.key}
                                        styles={{ marginLeft: 0, marginRight: '0.5rem' }}
                                        message={
                                            <span style={{ verticalAlign: '0.5px' }}>
                                                {category?.icon}
                                                {category?.label}
                                            </span>
                                        }
                                    />
                                );
                            })}
                        </Col>
                    </Row>
                }
            />
            {missingRequiredAddOn && !loading ? (
                <Button
                    size='small'
                    type='primary'
                    onClick={() => {
                        navigate('/settings/account/plans/addons');

                        setOpen(false);
                    }}
                    disabled={disabled || interactionDisabled}
                >
                    <TbPuzzle />
                    Enable Add-On
                    <TbArrowRight
                        style={{
                            verticalAlign: '-0.2rem',
                        }}
                    />
                </Button>
            ) : loading ? (
                <Button size='small' type='dashed' disabled={true} loading={loading}>
                    Loading...
                </Button>
            ) : (
                <Button
                    size='small'
                    type='dashed'
                    disabled={missingRequiredAddOn || disabled || interactionDisabled || dataSourceUsage.reached}
                    title={dataSourceUsage.reached ? dataSourceUsage.reason : undefined}
                    loading={connecting}
                    onClick={() => void handleConnect()}
                >
                    {!connecting && <TbPlug size='1rem' style={{ verticalAlign: '-0.175rem' }} />}
                    {connecting ? 'Connecting...' : 'Connect'}
                    {!connecting && (
                        <TbArrowUpRight
                            size='1rem'
                            style={{
                                verticalAlign: '-0.2rem',
                                color: token.colorTextQuaternary,
                            }}
                        />
                    )}
                </Button>
            )}
        </List.Item>
    );
};

export default AddDataSourceListItem;
